import { amsVersion } from '../components/config/config';
import CompanyService from './CompanyService';
import find from 'ramda/src/find';
import propEq from 'ramda/src/propEq';
import * as R from 'ramda';
import createAxios from 'App/utils/createAxios';
import convertKeysToCamelCase from 'App/utils/keyConversion/convertKeysToCamelCase';
import serializeJQLike from 'App/utils/serializeJQLike';

const endpoint = `/ams/${amsVersion}`;

const { protocol, host, port } = window.location;
const therigyURL = `${protocol}//${host}${port ? `:${port}` : ''}`;
const phpRootRequest = createAxios({ baseURL: therigyURL });

class AssessmentService {
    static GENERAL_NAME = 'General';

    static axios = createAxios({
        baseURL: endpoint,
    });

    static getAssessment(assessmentId) {
        return this.axios.get(`/assessment/${assessmentId}`);
    }

    static getQuestions(assessmentId, params = {}) {
        return this.axios.get(`/assessment/${assessmentId}/questions`, { params });
    }

    static getQuestionsById(assessmentId, questionIds) {
        const url = `/assessment/${assessmentId}/questions`;
        return this.axios.post(url, { questionIds });
    }

    static getTherapyAssessments(therapyId, showRetiredAssessments = false) {
        let params = { isMobileEnabled: 'true' };
        if (showRetiredAssessments) {
            params = { ...params, isShowRetired: 'true' };
        }
        return this.axios.get(`/therapy/${therapyId}/assessments`, { params });
    }

    static getGeneralTherapyAssessments() {
        return CompanyService.getTherapyCategories()
            .then(R.compose(find(propEq(AssessmentService.GENERAL_NAME, 'name')), R.values))
            .then((general) => {
                if (general) {
                    return AssessmentService.getTherapyAssessments(general.id);
                }
            });
    }

    /**
     * Create new activity note status
     * @param activityId - The activity id.
     * @param note - The note object
     * @returns {Promise<ActivityNote>} - activity note.
     */
    static createActivityNoteStatus(activityId, note) {
        return this.axios.post(`/activity/${activityId}/status`, note);
    }

    static getAssessmentCategories() {
        return phpRootRequest.get('/stm/assessment_category.php?angular=true');
    }

    static getAssessmentPublisherList() {
        return phpRootRequest.get('/app.api.php/api/assessment/modified').then(convertKeysToCamelCase);
    }

    static publishAssessment(assessmentId) {
        return phpRootRequest.post(`/app.api.php/api/assessment/${assessmentId}/publish`);
    }

    static deleteAssessment(assessmentId, groupId) {
        return phpRootRequest.get('/stm/assessment_mgr.php', {
            params: {
                delete_poll: true,
                poll_id: assessmentId,
                group_id: groupId,
            },
        });
    }

    static createAssessmentSettings(categoryId, body) {
        return phpRootRequest.post(
            '/stm/assessment_edit.php',
            serializeJQLike({
                ...body,
                angular: true,
                group_id: categoryId,
                submit: true,
            })
        );
    }

    static getAssessmentSettings(categoryId, assessmentId) {
        return phpRootRequest
            .get(`/stm/assessment_edit.php`, {
                params: {
                    angular: true,
                    group_id: categoryId,
                    poll_id: assessmentId,
                },
            })
            .then(convertKeysToCamelCase);
    }

    static updateAssessmentSettings(categoryId, assessmentId, body) {
        return phpRootRequest.post(
            '/stm/assessment_edit.php',
            serializeJQLike({
                ...body,
                angular: true,
                group_id: categoryId,
                category_id: categoryId,
                poll_id: assessmentId,
                submit: true,
            })
        );
    }

    static getRiskOptions(assessmentId) {
        return phpRootRequest.get(`/app.api.php/api/assessment/questions/${assessmentId}/riskoptions`);
    }

    static getRiskDetails(assessmentId, riskId) {
        return phpRootRequest.get(`/ajax/risk/load_risk.php?poll_id=${assessmentId}&scoring_id=${riskId}`);
    }

    static saveRisk(assessmentId, scoring) {
        return phpRootRequest.get('/ajax/risk/save_risk.php', {
            params: {
                poll_id: assessmentId,
                scoring_json: JSON.stringify(scoring),
            },
        });
    }

    static deleteRisk(assessmentId, riskId) {
        return phpRootRequest.get(`/ajax/risk/delete_risk.php`, {
            params: {
                poll_id: assessmentId,
                scoring_id: riskId,
            },
        });
    }
}

export default AssessmentService;
